import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import {
  Button,
  ContentBox,
  ContentPage,
  DropdownAction,
  FullTable,
  NoResults,
  Notice,
  PageActions,
  Text,
} from "@ficatranquilo/contabilidade.com-ui";
import useService from "@hooks/useService";
import { escapeHtml, formatDateTime, usePageHeader } from "@components/ow";
import AlertFormModal from "./AlertFormModal";
import AlertViewModal from "@components/AlertViewModal";
import DeleteAlertModal from "./DeleteAlertModal";

// Comunicados da empresa (modelo Lista do DS OneWork). Só ADM (guard da rota).
// Todos vão para a empresa toda: o back não direciona por pessoa, e o Dashboard
// do colaborador mostra os 3 mais recentes.

const PAGE_SIZE = 25;

const BREADCRUMB = [{ page: "Comunicados", route: "/c/alerts" }];

const NoticeSpace = styled.div`
  margin-bottom: 20px;
`;

// Texto em até 2 linhas; o inteiro fica no title e no "Ver comunicado"
const Clamp = styled.div`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: anywhere;
`;

const EMPTY = { totalRegisters: 0, list: [], totalPages: 1 };

export default function Alerts() {
  const alertsService = useService("alerts");
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: PAGE_SIZE });
  const [paginationData, setPaginationData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  // Só a resposta da última requisição vale (trocas de página seguidas podem voltar fora de ordem)
  const lastRequest = useRef(0);
  // { mode: "create" | "edit" | "view" | "delete", alert }
  const [modal, setModal] = useState(null);

  usePageHeader({ title: "Comunicados", breadcrumb: BREADCRUMB });

  useEffect(() => {
    fetchAlerts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.pageIndex, pagination.pageSize]);

  async function fetchAlerts() {
    const request = ++lastRequest.current;
    setLoading(true);
    try {
      const result = await alertsService.list(`page=${pagination.pageIndex + 1}&limit=${pagination.pageSize}`);
      if (request !== lastRequest.current) return;
      if (!result?.data) throw new Error("Erro ao carregar comunicados");
      // Excluir o último da última página deixaria a página vazia
      if (!result.data.list.length && pagination.pageIndex > 0) {
        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
        return;
      }
      setPaginationData(result.data);
    } catch (err) {
      if (request !== lastRequest.current) return;
      toast.error(err?.response?.data?.msg || err?.message || "Erro ao carregar comunicados");
      setPaginationData(EMPTY);
    }
    setLoading(false);
  }

  const openCreate = () => setModal({ mode: "create" });
  const closeModal = () => setModal(null);

  function afterChange() {
    setModal(null);
    fetchAlerts();
  }

  const columns = [
    {
      id: "text",
      header: "Comunicado",
      size: 640,
      cell: ({ row }) => (
        <div>
          <Clamp title={row.original.text || ""}>
            <Text $text={escapeHtml(row.original.text || "-")} $variant="paragraph_small_medium" $color="N900" />
          </Clamp>
          {row.original.image_url ? <Text $text="Com imagem" $variant="caption_medium" $color="N400" /> : null}
        </div>
      ),
    },
    { id: "created_at", header: "Criado em", size: 180, cell: ({ row }) => formatDateTime(row.original.created_at) },
    {
      id: "actions",
      // O "Ações" da lib tem 70px; abaixo disso a seta sai da célula
      size: 80,
      minSize: 80,
      maxSize: 80,
      align: "right",
      cell: ({ row }) => (
        <DropdownAction
          options={[
            { label: "Ver comunicado", action: () => setModal({ mode: "view", alert: row.original }) },
            { label: "Editar texto", action: () => setModal({ mode: "edit", alert: row.original }) },
            { label: "Excluir", action: () => setModal({ mode: "delete", alert: row.original }), isImportant: true },
          ]}
        />
      ),
    },
  ];

  return (
    <ContentPage>
      <PageActions>
        <Button $text="Criar comunicado" $variant="primary" $action={openCreate} />
      </PageActions>

      <ContentBox>
        <NoticeSpace>
          <Notice
            $variant="gray"
            $text="Os comunicados aparecem para todos os colaboradores da empresa. Os 3 mais recentes ficam no Dashboard deles."
          />
        </NoticeSpace>

        <FullTable
          data={paginationData.list}
          columns={columns}
          rowCount={paginationData.totalRegisters}
          pagination={pagination}
          setPagination={setPagination}
          getRowId={(row) => String(row.id)}
          isLoading={loading}
          onRowClick={(alert) => setModal({ mode: "view", alert })}
          noResults={
            <NoResults notResultText="Nenhum comunicado por aqui" includeText="Criar comunicado" includeAction={openCreate} />
          }
        />
      </ContentBox>

      {modal?.mode === "create" || modal?.mode === "edit" ? (
        <AlertFormModal alert={modal.alert} onClose={closeModal} onSaved={afterChange} />
      ) : null}
      {modal?.mode === "view" ? <AlertViewModal alert={modal.alert} onClose={closeModal} /> : null}
      {modal?.mode === "delete" ? (
        <DeleteAlertModal alert={modal.alert} onClose={closeModal} onDeleted={afterChange} />
      ) : null}
    </ContentPage>
  );
}
